import { Stack } from '@mui/system';
import React, { useRef, useState } from 'react';
import { useDownloadTemplateQuery, useGetPreviewQuery } from '../features/templates/templateSlice';
import Loading from './Loading';

import { Stage, Layer, Rect, Transformer, Text, Image, Group } from 'react-konva';
import ModelEditor from './ModelEditor';
import { targetareaFields } from '../features/model/modelFields';
import { deepEqual } from '../helpers/objects';
import { Button, FormControlLabel, setRef, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useUpdateTemplateMutation } from '../features/model/templateSlice';
import URLImage from './URLImage';
import { useUpdateSKUImageMutation } from '../features/model/skuimageSlice';
import { CheckBox } from '@mui/icons-material';

// Also from a konva example (for react + transform)
const TargetArea = ({ shapeProps, isSelected, onSelect, onChange, previewIdx }) => {
    const shapeRef = React.useRef();
    const trRef = React.useRef();
  
    React.useEffect(() => {
      if (isSelected) {
        // we need to attach transformer manually
        trRef.current.nodes([shapeRef.current]);
        trRef.current.getLayer().batchDraw();
      }
    }, [isSelected]);
  
    return (
      <React.Fragment>
        <Group
          onClick={onSelect}
          onTap={onSelect}
          ref={shapeRef}
          {...shapeProps}
          draggable
          onDragEnd={(e) => {
            const node = shapeRef.current;
            onChange({
              ...shapeProps,
              x: e.target.x(),
              y: e.target.y(),
              width: node.width(),
              height: node.height(),
              rotation: node.rotation()
            });
          }}
          onTransformEnd={(e) => {
            // transformer is changing scale of the node
            // and NOT its width or height
            // but in the store we have only width and height
            // to match the data better we will reset scale on transform end
            const node = shapeRef.current;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
  
            // we will reset it back
            node.scaleX(1);
            node.scaleY(1);
            onChange({
              ...shapeProps,
              x: node.x(),
              y: node.y(),
              // set minimal value
              width: Math.max(5, node.width() * scaleX),
              height: Math.max(5, node.height() * scaleY),
              rotation: node.rotation()
            });
          }}
        >
            <Rect width={shapeProps.width} height={shapeProps.height} stroke={shapeProps.stroke} previewIdx={previewIdx} />
            <Text text={shapeProps.area.label} fill="red" x={5} y={5} />
        </Group>
        {isSelected && (
          <Transformer
            anchorSize={7}
            ref={trRef}
            previewIdx={previewIdx}
            boundBoxFunc={(oldBox, newBox) => {
              // limit resize
              if (newBox.width < 5 || newBox.height < 5) {
                return oldBox;
              }
              return newBox;
            }}
          />
        )}
      </React.Fragment>
    );
};

function createRectangle(area, scale) {
  return {
    x: area.x * scale,
    y: area.y * scale,
    width: area.width * scale,
    height: area.height * scale,
    stroke: "red",
    id: area.name,
    area: area,
    rotation: 0.0
  }
}

function photoRectangle(template, scale) {
  // We shall not talk about this
  let w = template.width * scale;
  let h = template.height * scale;
  return {
    x: w*0.25 + Math.random() * 0.1,
    y: h*0.25 + Math.random() * 0.1,
    width: w*0.5 + Math.random() * 0.1,
    height: h*0.5 + Math.random() * 0.1,
    rotation: 0.0 + Math.random() * 0.1,
    stroke: "blue",
    id: "__image__",
    area: {default_text: "", label: "PHOTO"}
  }
}

function templateRectangles(template, scale, aligning) {
    if(!scale) return [];
    if(aligning) {
      return [photoRectangle(template, scale)];
    }
    var areas = JSON.parse(template.areas);
    var rects = Object.values(areas).map((area) => createRectangle(area, scale));
    return rects.sort((a, b) => ((b.width * b.height) - (a.width * a.height)));
}

function constructPreview(sku, template, side, scale, rectangles, imagepreview) {
  if (!template || !rectangles || !scale)
    return null;

  var values = {};
  var areas = {};

  values[side] = {};

  rectangles.forEach(rect => {
    values[side][rect.id] = {
      "type": "Text",
      "text": rect.area.default_text !== "" ? rect.area.default_text : rect.area.test_text
    };
    try {
      areas[rect.id] = JSON.parse(JSON.stringify(rect.area));
      areas[rect.id].x = rect.x / scale;
      areas[rect.id].y = rect.y / scale;
      areas[rect.id].width = rect.width / scale;
      areas[rect.id].height = rect.height / scale;
    } catch {

    }
  })
  if(imagepreview)
    return {
      template: template.name,
      sku: sku.code,
      values: values,
      areas: areas
    };
  return {
    template: template.name,
    values: values,
    areas: areas
  };
}
  
const TemplateAreasEditor = ({refresh, sku, template, skuimage, side, onChangeArea, onSelectArea, updateArea, aligning, imagepreview}) => {
    const { data: templateSVG, error, isLoading} = useDownloadTemplateQuery({name: template.svg_source})
    const [isAligning, setIsAligning] = useState(false);
    const [refreshed, setRefreshed] = useState(false);
    const aspect = template.width / template.height;
    const max_width = aspect > 1.0 ? 700 : 700 * aspect;
    const width = max_width;
    const height = max_width / aspect + 10;
    const [scale, setScale] = useState(max_width / template.width);
    const [rectangles, setRectangles] = React.useState(templateRectangles(template, scale, aligning));
    const [selected, selectShape] = React.useState(null);
    const preview_data = constructPreview(sku,template, side, scale, rectangles, imagepreview);
    const [previewIdx, setPreviewIdx] = useState(0);
    const { data: rendered, isLoading: previewLoading } = useGetPreviewQuery({side: side, size: 700, body: preview_data, idx: previewIdx}, {skip: preview_data == null});
    const [updateSkuImage] = useUpdateSKUImageMutation();
    const [alignX, setAlignX] = useState(null);
    const [alignY, setAlignY] = useState(null);
    const [alignS, setAlignS] = useState(null);
    const [alignA, setAlignA] = useState(null);

    if(aligning != isAligning) {
      setIsAligning(aligning);
      setRectangles(templateRectangles(template, scale, aligning));
    }
    
    if(refresh !== refreshed) {
      setRectangles(templateRectangles(template, scale, aligning));
      setRefreshed(refresh);
    }
    var newScale = max_width / template.width;
    if (newScale !== scale) {
        setScale(newScale);
    }
    if (isLoading) {
      return <Loading />;
    }
    if (error) {
      return <div>Error: {error.error}</div>
    }
    if (template === null) {
      return <div></div>
    }
    const imagedata = "data:image/svg+xml;utf8,"+encodeURIComponent(templateSVG);
    const previewdata = rendered ? rendered.img : null;

    const checkDeselect = (e) => {
        // deselect when clicked on empty area
        const clickedOnEmpty = e.target === e.target.getStage() || e.target.id().includes("template");
        if (clickedOnEmpty) {
          selectShape(null);
          onSelectArea(null);
        }
    };

    if(updateArea && rectangles) {
      var changed = false;
      var rects = [];

      rects = rectangles.map((rect) => {
        if(rect.id === updateArea[0]) {
          if(!deepEqual(rect.area, updateArea)) {
            rect.area = updateArea[1];
            changed = true;
          }
        }
        return rect;
      });
    
      if(changed) {
        setRectangles(rects);
      }

    }

    const image_meta = skuimage ? JSON.parse(skuimage.metadata.replaceAll("'", "\"")) : {"alignment": {"x": 0, "y": 0, "scale": 1.0, "angle": 0.0}};
    if (!alignX) {
      setAlignX(image_meta["alignment"]["x"].toString());
      setAlignY(image_meta["alignment"]["y"].toString());
      setAlignS(image_meta["alignment"]["scale"].toString());
      setAlignA(image_meta["alignment"]["angle"].toString());
    }


    function setAlignmentValues(values) {
      let new_meta = {...image_meta};
      new_meta["alignment"] = values;
      let new_skuimage = {...skuimage};
      new_skuimage.metadata = JSON.stringify(new_meta);
      updateSkuImage({skuimage_id: skuimage.id, data: new_skuimage});
      setPreviewIdx(previewIdx + 1);
    }

    function adjustAlignment(deltaX, deltaY, deltaScl, deltaRot) {
      // Alignment is calculated somewhat by taking the pixel size of the photo (!?) and multiplying by 96.0 as a DPI value.
      // This is accidental, wrong and we need a new way of storing alignments.
      let new_meta = {...image_meta};
      let oldX = image_meta["alignment"]["x"];
      let oldY = image_meta["alignment"]["y"];
      let oldScale = image_meta["alignment"]["scale"];
      let oldRotation = image_meta["alignment"]["angle"];

      // We can't rotate _and_ move simultaneously.
      let newAlignment = {
        x: oldX + deltaX / scale * 96.0,
        y: oldY + deltaY / scale * 96.0,
        scale: oldScale * deltaScl,
        angle: oldRotation
      };

      new_meta["alignment"] = newAlignment;
      let new_skuimage = {...skuimage};
      new_skuimage.metadata = JSON.stringify(new_meta);
      updateSkuImage({skuimage_id: skuimage.id, data: new_skuimage});
      setPreviewIdx(previewIdx + 1);
    }

    return <Stack direction="column">
      <Stage width={width} height={height} style={{marginTop: 10, marginBottom: 10, backgroundColor: "#333333"}}
            onMouseDown={checkDeselect}
            onTouchStart={checkDeselect}
        >
        <Layer>
          <URLImage src={previewdata} width={width} height={width / aspect} id="template_preview"  flipped={template.flipped} />
        </Layer>
        <Layer>
          <URLImage src={imagedata} width={width} height={width / aspect} id="template_img" />
        </Layer>
        <Layer>
        {rectangles.map((rect, i) => <TargetArea
              key={`area_${i}`}
              shapeProps={rect}
              isSelected={aligning ? true : rect.id === (selected ? selected.id : null)}
              onSelect={() => {
                selectShape(rect);
                onSelectArea(rect.area);
              }}
              previewIdx={previewIdx}
              onChange={(newAttrs) => {
                const rects = rectangles.slice();
                let oldAttrs = rects[i];
                let scaledAttrs = {...newAttrs, x: newAttrs.x / scale, y: newAttrs.y / scale, width: newAttrs.width / scale, height: newAttrs.height / scale}
                if(newAttrs.id === "__image__") {
                  // photo alignment adjust
                  const rects = rectangles.slice();
                  rects[i] = photoRectangle(template, scale);
                  setRectangles(rects);
                  let deltaX = newAttrs.x - oldAttrs.x;
                  let deltaY = newAttrs.y - oldAttrs.y;
                  let deltaScl = newAttrs.width / oldAttrs.width;
                  let deltaRot = newAttrs.rotation - oldAttrs.rotation;
                  adjustAlignment(deltaX, deltaY, deltaScl, deltaRot);
                } else {
                  rects[i] = newAttrs;
                  setRectangles(rects);  
                  onChangeArea(newAttrs.id, scaledAttrs);
                }
              }}/>
        )}
        </Layer>
      </Stage>
      </Stack>
}

const TemplateToolbar = ({template, onTool, areaSelected, aligning, imagepreview}) => {
  var buttons = [<Button key="add" variant="contained" onClick={() => {onTool("add")}}><AddIcon /> Add</Button>];
  if(areaSelected) {
    buttons.push(<Button key="delete" variant="contained" onClick={() => {onTool("delete")}}><DeleteIcon /> Delete</Button>);
    //buttons.push(<Button key="clone" variant="contained" onClick={() => {onTool("clone")}}><ContentCopyIcon /> Clone</Button>);
  } else {

  }
  buttons.push(<Button key="save" variant="contained" onClick={() => {onTool("save")}}><SaveIcon /> Save</Button>);
  buttons.push(<Button key="align" variant={aligning ? "outlined" : "contained"} onClick={() => {onTool("align")}}>Align</Button>);
  if(!aligning){
    buttons.push(<Button key="imagepreview" variant={imagepreview ? "contained" : "outlined"} onClick={() => {onTool("imagepreview")}}>Image preview</Button>);
  }
  return <Stack direction="row" spacing={1}>
    {buttons}
  </Stack>;
}

function generateRandomString(len) {
  let result = "";
  const letters = "abcdefghijklmnopqrstuvwxyz";
  for (let i = 0; i < len; i++) {
    result += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  return result;
}

const TemplateEditor = ({sku, template, skuimage, side}) => {
  const [currentTemplate, setCurrentTemplate] = useState(template);
  const [selectedArea, selectArea] = useState(null);
  const [updateArea, setUpdateArea] = useState(null);
  const [refresher, setRefresher] = useState(false);
  const [aligning, setAligning] = useState(false);
  const [imagepreview, setImagepreview] = useState(true);
  const [saveTemplate] = useUpdateTemplateMutation();

  function areaChanged(area_id, data) {
    var area_data = JSON.parse(currentTemplate.areas);
    Object.values(area_data).forEach((area) => {
      if(area.name === area_id) {
        area.x = data.x;
        area.y = data.y;
        area.width = data.width;
        area.height = data.height;
      }
    })
    setCurrentTemplate({...currentTemplate, areas: JSON.stringify(area_data)});
  }
  function areaSelected(area) {
    selectArea(area);
  }
  function areaDataChanged(name, value) {
    if(!(name in selectedArea)) {
      console.log("Unknown data field");
    } else {
      if(!deepEqual(selectedArea[name], value)) {
        let old_value = (name in selectedArea) ? selectedArea[name] : "";
        selectedArea[name] = value;
        var area_data = JSON.parse(currentTemplate.areas);
        setUpdateArea([selectedArea, value]);
        // update areas
        if(name === "name") {
          // also clear out the old name
          delete(area_data[old_value]);
        }
        area_data[selectedArea.name] = selectedArea;
        setCurrentTemplate({...currentTemplate, areas: JSON.stringify(area_data)});
      }
    }
  }
  function invokeTool(name) {
    if(name === "add") {
      let new_area_name = "target_" + generateRandomString(5);
      var area_data = JSON.parse(currentTemplate.areas);
      let new_area = {
        "name": new_area_name,
        "label": "New area",
        "x": 0,
        "y": 0,
        "width": 1.0,
        "height": 1.0
      }
      area_data[new_area_name] = new_area;
      setCurrentTemplate({...currentTemplate, areas: JSON.stringify(area_data)});
      //setAddArea([new_area]);
      setRefresher(!refresher);
    }
    if(name==="align") {
      setAligning(!aligning);
      setRefresher(!refresher);
    }
    if(name=="imagepreview") {
      setImagepreview(!imagepreview);
      setRefresher(!refresher);
    }
    if(name==="delete") {
      var area_data = JSON.parse(currentTemplate.areas);
      if(selectedArea.name in area_data) {
        delete(area_data[selectedArea.name]);
        setCurrentTemplate({...currentTemplate, areas: JSON.stringify(area_data)});
        setRefresher(!refresher);
      }
    }
    if(name === "save") {
      // save changes
      saveTemplate({template_id: currentTemplate.id, data: currentTemplate});
    }
  }
  if (!currentTemplate) {
    return <></>;
  }

  let tae = <TemplateAreasEditor key={aligning ? "tae_align": "tae_edit"} refresh={refresher} sku={sku} template={currentTemplate} skuimage={skuimage} side={side} onSelectArea={areaSelected} onChangeArea={areaChanged} updateArea={updateArea} aligning={aligning} imagepreview={imagepreview}/>;
  let modeledit = <ModelEditor data={selectedArea} modelFunc={targetareaFields} onChange={areaDataChanged} />;
  let toolbar = <TemplateToolbar template={currentTemplate} onTool={invokeTool} areaSelected={selectedArea !== null} aligning={aligning} imagepreview={imagepreview} />;

  // Two layouts: vertical, horizontal
  if(template.width >= template.height) {
    return <Stack direction="column" spacing={1}>
        {tae}
        {toolbar}
        {modeledit}
    </Stack>;
  } else {
    return <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={2}>
          {tae}
          <Stack direction="column" spacing={2}>
            {modeledit}
          </Stack>
        </Stack>
        {toolbar}
    </Stack>;
  }
}

export default TemplateEditor;
